import { useBlockExplorerLink } from '@/domain/hooks/useBlockExplorerLink'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { formatCurrency } from '@coingecko/cryptoformat'
import { cva } from 'class-variance-authority'
import {
  Building2,
  Car,
  Check,
  ChevronDown,
  Copy,
  CreditCard,
  ExternalLink,
  Fuel,
  Heart,
  MapPin,
  Monitor,
  Plane,
  Scissors,
  ShoppingBag,
  ShoppingCart,
  Utensils,
  Wifi,
  Wrench,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  type SettlementStatus,
  Transaction,
  TransactionType,
  type UserMessage,
  useTransactionHistory,
} from '../../hooks/useTransactionHistory'
import { useSelectedCard } from '../../contexts/SelectedCardContext'
import { exportTransactionsToCSV } from '../../utils/exportCSV'
import { SpendingInsights } from '../SpendingInsights'
import { createSmartGet } from '../../hooks/useAuthenticatedMutation'
import { CARD_API_ENDPOINT } from '@/config/consts'
import { decodeOnchainRevert } from '@/lib/errors/parser'

interface OnchainTransaction {
  id: string
  status: string
  error_details?: string
  revert_reason?: string
  revert_data?: string
  /**
   * Backend-classified structured error message. Preferred over
   * decodeOnchainRevert when present — it's the same string rendered in the
   * admin panel and sent via Telegram, so users see consistent copy.
   */
  error_message?: UserMessage
}

const settlementBadgeVariants = cva('inline-flex rounded-[6px] border px-2 py-1', {
  variants: {
    variant: {
      healthy: 'border-green-400/30 bg-green-400/15 text-green-400',
      processing: 'border-amber-400/30 bg-amber-400/15 text-amber-400',
      attention: 'border-red-400/30 bg-red-400/15 text-red-400',
    },
  },
  defaultVariants: {
    variant: 'processing',
  },
})

const settlementBadgeLabel: Record<SettlementStatus, string> = {
  healthy: 'Settled',
  processing: 'Processing',
  attention: 'Failed',
}

const typeBadgeVariants = cva('inline-flex rounded-[6px] border px-1.5 py-0.5 text-xs', {
  variants: {
    variant: {
      refund: 'border-green-400/30 bg-green-400/15 text-green-400',
      adjustment: 'border-purple-400/30 bg-purple-400/15 text-purple-400',
      fee: 'border-orange-400/30 bg-orange-400/15 text-orange-400',
      withdraw: 'border-blue-400/30 bg-blue-400/15 text-blue-400',
    },
  },
})

type SortField = 'created_at' | 'amount' | 'status'
type SortDirection = 'asc' | 'desc'

// Parse MCC from either a raw code ("5411") or enriched category ("5411 - Grocery Stores...")
function parseMcc(mccOrCategory?: string): number | null {
  if (!mccOrCategory) return null
  const match = mccOrCategory.match(/^(\d{4})/)
  if (!match) return null
  const code = Number.parseInt(match[1]!, 10)
  return Number.isNaN(code) ? null : code
}

export function getMccIcon(mcc?: string) {
  if (!mcc) return CreditCard

  const code = parseMcc(mcc)
  if (code === null) return CreditCard

  // Grocery & supermarkets
  if (code >= 5411 && code <= 5499) return ShoppingCart
  // Restaurants & food
  if (code >= 5812 && code <= 5814) return Utensils
  // Gas stations
  if (code >= 5541 && code <= 5542) return Fuel
  // Airlines & travel
  if ((code >= 3000 && code <= 3999) || code === 4511) return Plane
  // Hotels & lodging
  if (code >= 7011 && code <= 7033) return Building2
  // Taxis & rideshare
  if (code === 4121 || code === 4131) return Car
  // Department stores
  if (code >= 5311 && code <= 5399) return ShoppingBag
  // Electronics & computers
  if (code >= 5732 && code <= 5734) return Monitor
  // Health & pharmacies
  if (code === 5912) return Heart
  if (code >= 8011 && code <= 8099) return Heart
  // Hardware & home improvement
  if (code >= 5200 && code <= 5261) return Wrench
  // Digital goods & subscriptions
  if (code === 5815 || code === 5816 || code === 5818) return Wifi
  // Beauty & personal care
  if (code >= 7230 && code <= 7299) return Scissors

  return CreditCard
}

function getMerchantLocation(merchant: { city?: string; country?: string }): string | null {
  if (merchant.city && merchant.country) return `${merchant.city}, ${merchant.country}`
  return merchant.city || merchant.country || null
}

function MerchantIcon({
  icon,
  name,
  mcc,
  category,
}: {
  icon?: string
  name: string
  mcc?: string
  category?: string
}) {
  const [imgError, setImgError] = useState(false)
  const FallbackIcon = getMccIcon(mcc ?? category)

  if (icon && !imgError) {
    return (
      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full">
        <img src={icon} alt={name} className="h-10 w-10 rounded-full object-cover" onError={() => setImgError(true)} />
      </div>
    )
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
      <FallbackIcon className="h-5 w-5 text-muted-foreground" />
    </div>
  )
}

function formatDateTime(dateString: string) {
  const date = new Date(dateString)
  return {
    date: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
    time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    full: date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  }
}

function TransactionHistorySkeleton() {
  const skeletonRows = Array.from({ length: 5 }, (_, i) => i)

  return (
    <div className="flex flex-col gap-6">
      <Panel.Wrapper>
        <Panel.Content className="flex items-center justify-between p-6">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-16" />
        </Panel.Content>
      </Panel.Wrapper>

      <Panel.Wrapper>
        <Panel.Content className="p-0">
          <div className="grid grid-cols-4 gap-4 border-white/10 border-b p-4">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <div className="text-right">
              <Skeleton className="ml-auto h-4 w-12" />
            </div>
          </div>

          {skeletonRows.map((index) => (
            <div key={index} className="grid grid-cols-4 gap-4 border-white/10 border-b p-4 last:border-b-0">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-20" />
              </div>
              <Skeleton className="h-4 w-16" />
              <div className="flex flex-col gap-2">
                <Skeleton className="h-6 w-16 rounded-[6px]" />
              </div>
              <div className="text-right">
                <Skeleton className="ml-auto h-4 w-16" />
                <Skeleton className="mt-1 ml-auto h-3 w-12" />
              </div>
            </div>
          ))}
        </Panel.Content>
      </Panel.Wrapper>
    </div>
  )
}

const timelineStepVariants = cva('flex gap-2', {
  variants: {
    variant: {
      default: 'items-center',
      compact: 'flex-col',
    },
  },
})

function TimelineStep({
  label,
  dateString,
  variant = 'default',
}: { label: string; dateString?: string | null; variant?: 'default' | 'compact' }) {
  const formatted = dateString ? formatDateTime(dateString).full : null

  return (
    <div className={cn(timelineStepVariants({ variant }))}>
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'h-2.5 w-2.5 flex-shrink-0 rounded-full',
            formatted ? 'bg-green-400' : 'bg-muted-foreground/30',
          )}
        />
        <Typography variant="p" className="w-16 text-muted-foreground text-xs">
          {label}
        </Typography>
      </div>
      <Typography variant="p" className="text-foreground text-sm">
        {formatted ?? '—'}
      </Typography>
    </div>
  )
}

const detailRowVariants = cva('flex', {
  variants: {
    variant: {
      default: 'justify-between gap-2',
      compact: 'mb-2 flex-col gap-1',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const detailRowValueVariants = cva('text-foreground text-sm', {
  variants: {
    variant: {
      default: 'text-right',
      compact: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

function CopyID({ id, variant }: { id: string; variant?: 'default' | 'compact' }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(id)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleCopy}
      className={cn(
        detailRowValueVariants({ variant }),
        'group flex items-center gap-1.5 font-mono text-sm transition-colors hover:text-foreground',
      )}
      title="Copy full ID"
    >
      <span>{id.slice(0, 12)}...</span>
      {copied ? (
        <Check className="h-3 w-3 text-green-400" />
      ) : (
        <Copy className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-60" />
      )}
    </button>
  )
}

function TransactionDetailPanel({
  transaction,
  variant = 'default',
}: { transaction: Transaction; variant?: 'default' | 'compact' }) {
  // Card transactions settle on HyperEVM (chain 999)
  const blockExplorerUrl = useBlockExplorerLink(999)

  const isFailed = transaction.onchain_status === 'failed' || transaction.flow === 'declined'
  const { data: onchainResponse } = useQuery<{ data: OnchainTransaction }>({
    queryKey: ['card-onchain-tx', transaction.id],
    queryFn: async () => {
      const url = `${CARD_API_ENDPOINT}/v1/vendor/cards/transactions/${transaction.id}/onchain`
      return createSmartGet(url)()
    },
    enabled: isFailed,
    staleTime: 1000 * 60 * 5,
  })
  const onchainDetail = onchainResponse?.data
  // Prefer the backend-classified structured message (same copy rendered in
  // admin panel + Telegram). Fall back to client-side decoding only when the
  // backend can't classify the revert — ensures the three surfaces stay in
  // sync.
  const onchainErrorBody =
    onchainDetail?.error_message?.body ||
    decodeOnchainRevert(onchainDetail?.revert_data, onchainDetail?.revert_reason)
  const onchainErrorTitle = onchainDetail?.error_message?.title || 'Revert Reason'
  const shouldPreferOnchainFailure =
    !!onchainErrorBody &&
    (
      !transaction.declined_message?.body ||
      transaction.declined_message.code === 'UNKNOWN_ERROR_RETRY' ||
      transaction.declined_message.code === 'DECLINED_BY_ISSUER'
    )
  const preferredDeclineTitle =
    shouldPreferOnchainFailure
      ? onchainErrorTitle
      : (transaction.declined_message?.title || 'Declined Reason')
  const preferredDeclineBody =
    shouldPreferOnchainFailure
      ? onchainErrorBody
      : (transaction.declined_message?.body || transaction.declined_reason)

  const hasLocalCurrency =
    transaction.local_currency &&
    transaction.currency &&
    transaction.local_currency !== transaction.currency &&
    transaction.local_amount != null

  const hasOnchain = !!(transaction.onchain_tx_hash || transaction.onchain_status || onchainErrorBody)

  return (
    <div className="border-border border-b bg-muted/20 px-4 py-4">
      <div className="grid grid-cols-3 gap-6">
        <div>
          <Typography variant="p" className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
            Details
          </Typography>
          <div className="flex flex-col gap-1.5">
            <div className={detailRowVariants({ variant })}>
              <Typography variant="p" className="text-muted-foreground text-sm">
                Type
              </Typography>
              <Typography variant="p" className={cn(detailRowValueVariants({ variant }), 'capitalize')}>
                {transaction.type}
              </Typography>
            </div>
            <div className={detailRowVariants({ variant })}>
              <Typography variant="p" className="text-muted-foreground text-sm">
                ID
              </Typography>
              <CopyID id={transaction.id} variant={variant} />
            </div>
            {transaction.merchant.category && (
              <div className={detailRowVariants({ variant })}>
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Category
                </Typography>
                <Typography variant="p" className={cn(detailRowValueVariants({ variant }), 'capitalize')}>
                  {transaction.merchant.category}
                </Typography>
              </div>
            )}
            {getMerchantLocation(transaction.merchant) && (
              <div className={detailRowVariants({ variant })}>
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Location
                </Typography>
                <Typography variant="p" className={cn(detailRowValueVariants({ variant }))}>
                  {getMerchantLocation(transaction.merchant)}
                </Typography>
              </div>
            )}
            {(preferredDeclineBody || transaction.declined_reason?.trim()) && (
              <div className={detailRowVariants({ variant })}>
                <Typography variant="p" className="flex-shrink-0 text-muted-foreground text-sm">
                  {preferredDeclineTitle}
                </Typography>
                <Typography variant="p" className={cn(detailRowValueVariants({ variant }), 'text-red-400')}>
                  {/* Prefer the most specific backend-owned explanation:
                      generic decline copy should yield to a richer onchain
                      failure message when settlement/auth failed downstream. */}
                  {preferredDeclineBody}
                </Typography>
              </div>
            )}
            {transaction.review_reason && (
              <div className={detailRowVariants({ variant })}>
                <Typography variant="p" className="flex-shrink-0 text-muted-foreground text-sm">
                  Review Reason
                </Typography>
                <Typography variant="p" className={cn(detailRowValueVariants({ variant }), 'text-amber-400')}>
                  {transaction.review_reason.replace(/^\["|"\]$/g, '')}
                </Typography>
              </div>
            )}
          </div>
        </div>

        {hasLocalCurrency && (
          <div>
            <Typography variant="p" className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
              Currency Conversion
            </Typography>
            <div className="flex flex-col gap-1.5">
              <div className={detailRowVariants({ variant })}>
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Local Amount
                </Typography>
                <Typography variant="p" className="text-foreground text-sm">
                  {formatCurrency(transaction.local_amount! / 100, transaction.local_currency, 'en', false, {
                    decimalPlaces: 2,
                  })}
                </Typography>
              </div>
              <div className={detailRowVariants({ variant })}>
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Settlement
                </Typography>
                <Typography variant="p" className={cn(detailRowValueVariants({ variant }))}>
                  {formatCurrency(transaction.amount / 100, transaction.currency, 'en', false, {
                    decimalPlaces: 2,
                  })}
                </Typography>
              </div>
              {transaction.local_amount && transaction.local_amount > 0 && (
                <div className={detailRowVariants({ variant })}>
                  <Typography variant="p" className="text-muted-foreground text-sm">
                    Rate
                  </Typography>
                  <Typography variant="p" className={cn(detailRowValueVariants({ variant }), 'font-mono')}>
                    1 {transaction.currency} ≈ {(transaction.local_amount / transaction.amount).toFixed(4)}{' '}
                    {transaction.local_currency}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={cn(!hasLocalCurrency && 'col-start-3')}>
          <Typography variant="p" className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
            Timeline
          </Typography>
          <div className="flex flex-col gap-1">
            <TimelineStep label="Created" dateString={transaction.created_at} variant={variant} />
            <TimelineStep label="Authorized" dateString={transaction.authorized_at} variant={variant} />
            <TimelineStep label="Posted" dateString={transaction.posted_at} variant={variant} />
          </div>
        </div>
      </div>

      {hasOnchain && (
        <div className="mt-4 border-border border-t pt-4">
          <Typography variant="p" className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
            On-Chain
          </Typography>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between rounded-md border border-border bg-background/50 px-3 py-2">
              <div className="flex items-center gap-3">
                {transaction.onchain_status && (
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-xs capitalize',
                      transaction.onchain_status === 'completed'
                        ? 'bg-green-400/15 text-green-400'
                        : transaction.onchain_status === 'failed'
                          ? 'bg-red-400/15 text-red-400'
                          : 'bg-amber-400/15 text-amber-400',
                    )}
                  >
                    {transaction.onchain_status}
                  </span>
                )}
              </div>
              {transaction.onchain_tx_hash && (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-muted-foreground text-xs">
                    {transaction.onchain_tx_hash.slice(0, 10)}...{transaction.onchain_tx_hash.slice(-6)}
                  </span>
                  {blockExplorerUrl && (
                    <a
                      href={`${blockExplorerUrl}/tx/${transaction.onchain_tx_hash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
            {onchainErrorBody && (
              <div className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2">
                <Typography variant="p" className="mb-0.5 text-muted-foreground text-xs">
                  {onchainErrorTitle}
                </Typography>
                <Typography variant="p" className="break-all text-red-400 text-xs">
                  {onchainErrorBody}
                </Typography>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function SortHeader({
  label,
  field,
  currentField,
  currentDirection,
  onSort,
  align = 'left',
}: {
  label: string
  field: SortField
  currentField: SortField
  currentDirection: SortDirection
  onSort: (field: SortField) => void
  align?: 'left' | 'right'
}) {
  const isActive = currentField === field

  return (
    <button
      onClick={() => onSort(field)}
      className={cn(
        'flex items-center gap-1 font-medium text-muted-foreground text-xs transition-colors hover:text-foreground',
        align === 'right' && 'ml-auto',
        isActive && 'text-foreground',
      )}
    >
      {label}
      <ChevronDown
        className={cn(
          'h-3 w-3 transition-transform',
          isActive && currentDirection === 'asc' && 'rotate-180',
          !isActive && 'opacity-0',
        )}
      />
    </button>
  )
}

export function TransactionRow({
  transaction,
  isExpanded,
  onToggle,
  detailVariant,
}: {
  transaction: Transaction
  isExpanded: boolean
  onToggle: () => void
  detailVariant?: 'default' | 'compact'
}) {
  const isCredit = transaction.flow === 'refund'
  const isFailed = transaction.settlement_status === 'attention'
  const settlementVariant = (transaction.settlement_status ?? 'processing') as SettlementStatus
  const isNonPurchase = transaction.type !== TransactionType.SPEND && transaction.type !== TransactionType.PURCHASE
  const { date, time } = formatDateTime(transaction.created_at)
  const location = getMerchantLocation(transaction.merchant)

  // For failed/declined txs show attempted amount (authorized_amount), not the 0 charged amount
  const displayAmount = isFailed ? transaction.authorized_amount ?? 0 : transaction.amount

  const hasLocalCurrency =
    transaction.local_currency &&
    transaction.currency &&
    transaction.local_currency !== transaction.currency &&
    transaction.local_amount != null

  return (
    <>
      <div
        onClick={onToggle}
        className={cn(
          'flex cursor-pointer items-center justify-between border-border border-b px-4 py-3 transition-colors hover:bg-muted/30',
          isExpanded && 'bg-muted/20',
        )}
      >
        <div className="flex items-center gap-3">
          <MerchantIcon
            icon={transaction.merchant.icon}
            name={transaction.merchant.name}
            mcc={transaction.merchant.mcc}
            category={transaction.merchant.category}
          />
          <div>
            <div className="flex items-center gap-2">
              <Typography variant="p" className="font-medium capitalize">
                {transaction.merchant.name}
              </Typography>
              {isNonPurchase && (
                <span
                  className={typeBadgeVariants({
                    variant: transaction.type as 'refund' | 'adjustment' | 'fee' | 'withdraw',
                  })}
                >
                  {transaction.type}
                </span>
              )}
            </div>
            <Typography variant="p" className="text-muted-foreground text-xs">
              {date} • {time}
              {location && (
                <span className="ml-1.5">
                  <MapPin className="mr-0.5 inline h-3 w-3" />
                  {location}
                </span>
              )}
            </Typography>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <Typography
              variant="p"
              className={cn(
                'font-medium text-sm',
                isCredit ? 'text-green-400' : isFailed ? 'text-muted-foreground line-through' : 'text-foreground',
              )}
            >
              {isCredit ? '+' : ''}
              {formatCurrency(displayAmount / 100, transaction.currency, 'en', false, {
                decimalPlaces: 2,
              })}
            </Typography>
            {hasLocalCurrency && !isFailed && (
              <Typography variant="p" className="text-muted-foreground text-xs">
                (
                {formatCurrency(transaction.local_amount! / 100, transaction.local_currency, 'en', false, {
                  decimalPlaces: 2,
                })}
                )
              </Typography>
            )}
          </div>

          <div className={cn(settlementBadgeVariants({ variant: settlementVariant }))}>
            <Typography variant="span" className="font-normal text-inherit text-xs leading-tight">
              {settlementBadgeLabel[settlementVariant]}
            </Typography>
          </div>

          <ChevronDown
            className={cn('h-4 w-4 text-muted-foreground transition-transform', isExpanded && 'rotate-180')}
          />
        </div>
      </div>

      {isExpanded && <TransactionDetailPanel transaction={transaction} variant={detailVariant} />}
    </>
  )
}

export function TransactionHistoryTab() {
  const { selectedCard: activeCard } = useSelectedCard()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useTransactionHistory(
    activeCard?.cardID || '',
  )

  const allTransactions = useMemo(() => {
    return data?.pages.flatMap((page) => page.data?.transactions ?? []) ?? []
  }, [data])

  const sortedTransactions = useMemo(() => {
    const sorted = [...allTransactions]
    sorted.sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
        case 'amount':
          comparison = a.amount - b.amount
          break
        case 'status':
          comparison = (a.settlement_status ?? 'processing').localeCompare(b.settlement_status ?? 'processing')
          break
      }
      return sortDirection === 'asc' ? comparison : -comparison
    })
    return sorted
  }, [allTransactions, sortField, sortDirection])

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortField(field)
        setSortDirection('desc')
      }
    },
    [sortField],
  )

  const handleToggle = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }, [])

  const handleExportCSV = useCallback(() => {
    if (allTransactions.length > 0) {
      exportTransactionsToCSV(allTransactions)
    }
  }, [allTransactions])

  if (isLoading) {
    return <TransactionHistorySkeleton />
  }

  if (error) {
    return (
      <Panel.Wrapper>
        <Panel.Content className="p-8">
          <Typography variant="p" className="text-center text-red-400">
            Failed to load transactions
          </Typography>
        </Panel.Content>
      </Panel.Wrapper>
    )
  }

  if (!activeCard?.cardID) {
    return (
      <Panel.Wrapper>
        <Panel.Content className="p-8">
          <Typography variant="p" className="text-center text-muted-foreground">
            Card information not available
          </Typography>
        </Panel.Content>
      </Panel.Wrapper>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <SpendingInsights transactions={allTransactions} onExport={handleExportCSV} />

      <Panel.Wrapper>
        <Panel.Content className="p-0">
          <div className="flex items-center justify-between border-border border-b px-4 py-3">
            <SortHeader
              label="Date"
              field="created_at"
              currentField={sortField}
              currentDirection={sortDirection}
              onSort={handleSort}
            />
            <div className="flex items-center gap-6">
              <SortHeader
                label="Amount"
                field="amount"
                currentField={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortHeader
                label="Status"
                field="status"
                currentField={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                align="right"
              />
            </div>
          </div>

          {sortedTransactions.length === 0 ? (
            <div className="py-12 text-center">
              <Typography variant="p" className="text-muted-foreground">
                No transactions found
              </Typography>
            </div>
          ) : (
            sortedTransactions.map((tx) => (
              <TransactionRow
                key={tx.id}
                transaction={tx}
                isExpanded={expandedId === tx.id}
                onToggle={() => handleToggle(tx.id)}
              />
            ))
          )}
        </Panel.Content>
      </Panel.Wrapper>

      {hasNextPage && (
        <Panel.Wrapper>
          <Panel.Content className="flex justify-center p-4">
            <Button onClick={() => fetchNextPage()} size="sm" disabled={isFetchingNextPage}>
              {isFetchingNextPage ? 'Loading...' : 'Load More Transactions'}
            </Button>
          </Panel.Content>
        </Panel.Wrapper>
      )}
    </div>
  )
}
